import {
  assign,
  createStateMachine,
  type EventObject,
  type InvokeDoneEvent,
} from '@heidi/flow/state-machine';

import type { OfflineSyncAutomaticRedriveReadiness } from '@/tauri/offline-recording/sync';

export interface ChronicleBulkSyncStartupInput {
  token: string;
  userId: string;
  deviceSerial: string | null;
  automaticTargetGeneration: number | null;
}

interface ChronicleBulkSyncStartupContext {
  input: ChronicleBulkSyncStartupInput | null;
  automaticTargetGeneration: number | null;
}

type ChronicleBulkSyncStartupEvent = EventObject &
  ({ type: 'START'; input: ChronicleBulkSyncStartupInput } | { type: 'RESET' });

const initialContext: ChronicleBulkSyncStartupContext = {
  input: null,
  automaticTargetGeneration: null,
};

const resetContext = assign<ChronicleBulkSyncStartupContext>(() => initialContext);

/**
 * Observable startup handshake required by the provider:
 * readiness snapshot → engine → binding key → token → guarded offline re-drive.
 * The interpreter owns cancellation and stale async settlements.
 */
export const chronicleBulkSyncStartupMachine = createStateMachine({
  id: 'chronicleBulkSyncStartup',
  initial: 'idle',
  context: initialContext,
  states: {
    idle: {
      on: {
        START: {
          target: 'readAutomaticReadiness',
          actions: assign<ChronicleBulkSyncStartupContext, ChronicleBulkSyncStartupEvent>(
            (_, event) => ({
              input: event.type === 'START' ? event.input : null,
              automaticTargetGeneration: null,
            })
          ),
        },
      },
    },
    readAutomaticReadiness: {
      on: { RESET: { target: 'idle', actions: resetContext } },
      invoke: {
        src: 'readAutomaticReadiness',
        onDone: {
          target: 'startEngine',
          actions: assign<
            ChronicleBulkSyncStartupContext,
            InvokeDoneEvent<OfflineSyncAutomaticRedriveReadiness | null>
          >((_, event) => ({
            automaticTargetGeneration: event.data?.targetGeneration ?? null,
          })),
        },
        // Readiness is optional; an IPC read failure must not block the engine.
        onError: 'startEngine',
      },
    },
    startEngine: {
      on: { RESET: { target: 'idle', actions: resetContext } },
      invoke: { src: 'startEngine', onDone: 'syncBindingCode', onError: 'failed' },
    },
    syncBindingCode: {
      on: { RESET: { target: 'idle', actions: resetContext } },
      invoke: {
        src: 'syncBindingCode',
        onDone: 'provisionToken',
        onError: 'provisionToken',
      },
    },
    provisionToken: {
      on: { RESET: { target: 'idle', actions: resetContext } },
      invoke: {
        src: 'provisionToken',
        onDone: 'requestAutomaticRedrive',
        onError: 'failed',
      },
    },
    requestAutomaticRedrive: {
      on: { RESET: { target: 'idle', actions: resetContext } },
      invoke: {
        src: 'requestAutomaticRedrive',
        onDone: 'done',
        onError: 'done',
      },
    },
    done: {
      type: 'final',
      on: { RESET: { target: 'idle', actions: resetContext } },
    },
    failed: {
      type: 'final',
      on: { RESET: { target: 'idle', actions: resetContext } },
    },
  },
});
